/**
 * User State Store
 *
 * 管理用戶狀態，提供 Vue reactive 的用戶資料，並與 authService 同步
 */

import { defineStore } from 'pinia'
import { authService, type AuthUser, type AuthEvent } from '@shared/services/auth/authService'
import type { App } from 'vue'

// ==================== 類型定義 ====================

export interface UserStateType {
  /** 當前用戶 */
  currentUser: AuthUser | null
  /** 認證是否就緒 */
  isReady: boolean
  /** 是否正在載入 */
  isLoading: boolean
}

// ==================== Store 定義 ====================

export const useUserStore = defineStore('userStore', {
  state: (): UserStateType => ({
    currentUser: null,
    isReady: false,
    isLoading: false,
  }),

  getters: {
    /** 用戶 ID (優先 uid，備用 id) */
    userId: (state): string | null => state.currentUser?.uid || state.currentUser?.id || null,

    /** 是否已登入 */
    isLoggedIn: (state): boolean => !!state.currentUser,

    /** 用戶 Email */
    email: (state): string | null => state.currentUser?.email || null,

    /** 顯示名稱 */
    displayName: (state): string | null => state.currentUser?.displayName || null,

    /** 用戶頭像 */
    photoURL: (state): string | null => state.currentUser?.photoURL || null,

    /** 上次選擇的組織 ID */
    lastSelectedOrgId: (state): string | null => state.currentUser?.lastSelectedOrgId || null,
  },

  actions: {
    /** 初始化：訂閱 authService 狀態變化 */
    initialize(): void {
      // 同步初始狀態
      this.currentUser = authService.currentUser
      this.isReady = authService.isAuthReady

      // 訂閱後續變化
      authService.onAuthStateChange((event: AuthEvent, user: AuthUser | null) => {
        this.currentUser = user
        this.isReady = true

        if (event === 'SIGNED_OUT') {
          this.currentUser = null
        }
      })
    },

    /** 設置用戶 */
    setUser(user: AuthUser | null): void {
      this.currentUser = user
    },

    /** 清除用戶 */
    clearUser(): void {
      this.currentUser = null
    },

    /** 設置載入狀態 */
    setLoading(loading: boolean): void {
      this.isLoading = loading
    },
  },
})

// ==================== Vue Plugin ====================

export const userStore = {
  install: (app: App): void => {
    const store = useUserStore()
    store.initialize()

    // 註冊全域屬性
    app.config.globalProperties.$user = store
    app.config.globalProperties.userStore = store
  },
}

export default userStore
